'use client';

import BingUETScript from 'app/components/Common/BingUETScript';
import FBPixelScript from 'app/components/Common/FBPixelScript';
import KlaviyoScript from 'app/components/Common/KlaviyoScript';
import useIdleLoading from 'app/hooks/useIdleLoading';
import { useSearchParams, usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import React, { FC, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { SUPPORTED_LANGUAGES } from 'app/locales/i18n';
import { emitGAEventHomePageView } from 'app/modules/ga-event.helpers';

interface RenderGAProps {
	uid: string;
	imid: string;
	reduxLng: string;
	isSearchFormPage: boolean;
}

const DynamicGoogleAnalytics = dynamic(() => import('@next/third-parties/google').then((mod) => mod.GoogleAnalytics), {
	ssr: false,
});

const RenderGA: FC<RenderGAProps> = ({ uid, imid, reduxLng, isSearchFormPage }) => {
	const isIdle = useIdleLoading();
	const [isGALoaded, setIsGALoaded] = useState(false);
	const params = useSearchParams();
	const pathname = usePathname();

	const urlLng = params?.get('lng') as string;
	const pathLng = pathname?.split('/')[1];
	const detectedPathLng = pathLng && SUPPORTED_LANGUAGES.includes(pathLng) ? pathLng : null;

	// Fallback: URL param → Pathname segment → Redux state → 'en'
	const siteLanguage = urlLng || detectedPathLng || reduxLng || 'en';
	// Last pathname for which home_page_view fired. Replaces the previous
	// one-shot boolean so the event re-fires on SPA navigation between
	// search-form pages instead of latching on the first load of any page.
	const lastHomePageViewPath = useRef<string | null>(null);

	useEffect(() => {
		if (isIdle && !isGALoaded) {
			let retries = 0;
			const MAX_RETRIES = 50; // Wait up to 5 seconds

			const checkGtagAndFireEvent = () => {
				if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
					setIsGALoaded(true);
				} else if (retries < MAX_RETRIES) {
					retries++;
					setTimeout(checkGtagAndFireEvent, 100);
				}
			};

			checkGtagAndFireEvent();
		}
	}, [isIdle, isGALoaded]);

	useEffect(() => {
		if (isGALoaded && typeof window !== 'undefined' && typeof window.gtag === 'function') {
			if (uid && imid) {
				window.gtag('set', 'user_id', uid + imid);
			}
			window.gtag('set', 'user_properties', {
				site_language: siteLanguage,
			});
		}
	}, [isGALoaded, uid, imid, siteLanguage]);

	useEffect(() => {
		// Reset when leaving a search-form page so a later SPA navigation back
		// to a form page (incl. the home route) fires home_page_view again.
		if (!isSearchFormPage) {
			lastHomePageViewPath.current = null;
			return;
		}

		if (
			isGALoaded &&
			typeof window !== 'undefined' &&
			typeof window.gtag === 'function' &&
			pathname &&
			lastHomePageViewPath.current !== pathname
		) {
			// Latch synchronously (not inside the deferred callback) so a re-run
			// of this effect before the idle callback fires cannot schedule a
			// second home_page_view for the same pathname.
			lastHomePageViewPath.current = pathname;
			const fireEvent = () => {
				emitGAEventHomePageView();
			};

			if (window.requestIdleCallback) {
				window.requestIdleCallback(fireEvent);
			} else {
				setTimeout(fireEvent, 0);
			}
		}
	}, [isGALoaded, isSearchFormPage, pathname]);

	return (
		isIdle && (
			<>
				<DynamicGoogleAnalytics dataLayerName="dataLayer" gaId={process.env.NEXT_PUBLIC_GA_ID as string} />
				<FBPixelScript pixelId="363719008945777" />
				<BingUETScript tagId="148000929" />
				<KlaviyoScript companyId="RPT2kL" />
			</>
		)
	);
};

const mapStateToProps = (state: any) => ({
	uid: state.base.uid,
	imid: state.base.imid,
	reduxLng: state.base.lng,
	isSearchFormPage: state.base.isSearchFormPage,
});

export default connect(mapStateToProps)(React.memo(RenderGA));
